import { Loading, Loadable } from '@/components/loaders';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { useGetAudienceQuery } from '@/services/endpoints/audiences';
import { usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import Form, { IChangeEvent } from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS } from '@/features/destinations/components/connectorFormConfig';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowFormData, FlowMappingsConfig, StepConfig } from './types';
import { templates, widgets } from './FormFields/StyledField';
import ErrorBoundary from '@/components/ErrorBoundary';
import { EmailTemplatePreview } from '../Email/TemplatePreview/EmailTemplatePreview';
import { TemplatePreview } from './TemplatePreview/TemplatePreview';
import { Button } from '@/lib';
import { StepsIndication } from '@/components/StepsIndication/StepsIndication';
import { enhanceFlowStepUiSchema, type HtmlBodyFieldMode } from './enhanceFlowStepUiSchema';

type MultiStepFlowFormProps = {
  id?: string;
  audienceId: string;
  connectorInstanceId: string;
  connectorFlowId: string;
  paramsConfig: Record<string, StepConfig>;
  onComplete: (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => void;
  showTemplatePreview?: boolean;
  templatePreviewVariant?: 'whatsapp' | 'email';
  isInAction?: boolean;
  htmlBodyFieldMode?: HtmlBodyFieldMode;
  lastStepActionLabel?: string;
};

const isSchemaAllHidden = (form: { schema?: RJSFSchema; uiSchema?: Record<string, unknown> } | undefined): boolean => {
  const props = (form?.schema as any)?.properties || {};
  const keys = Object.keys(props);
  if (keys.length === 0) return true;
  const ui = (form?.uiSchema as Record<string, any>) || {};
  return keys.every((k) => ui[k]?.['ui:widget'] === 'hidden');
};

const collectDefaults = (schema: RJSFSchema | undefined): FlowFormData => {
  const data: FlowFormData = {};
  const props = (schema as any)?.properties || {};
  for (const [key, prop] of Object.entries<any>(props)) {
    if (prop?.default !== undefined) data[key] = prop.default;
  }
  return data;
};

export const MultiStepFlowForm: FC<MultiStepFlowFormProps> = ({
  audienceId,
  connectorFlowId,
  connectorInstanceId,
  paramsConfig,
  onComplete,
  id = 'multi-step-flow',
  showTemplatePreview = false,
  templatePreviewVariant = 'whatsapp',
  isInAction = false,
  htmlBodyFieldMode = 'default',
  lastStepActionLabel,
}) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const [prepareStep, { isLoading: isLoadingStep }] = usePrepareConnectorFlowStepMutation();
  const dispatch = useAppDispatch();
  const {
    data: audience,
    isLoading: isAudienceLoading,
    isFetching: isAudienceFetching,
  } = useGetAudienceQuery(
    { workspaceId, audienceId },
    {
      skip: !workspaceId || !audienceId,
    }
  );

  const { currentLanguage } = useCompanyValues();

  // Get sorted step keys (step1, step2, step3, ...)
  const stepKeys = useMemo(() => {
    return Object.keys(paramsConfig)
      .filter((key) => /^step\d+$/.test(key))
      .sort((a, b) => parseInt(a.replace('step', '')) - parseInt(b.replace('step', '')));
  }, [paramsConfig]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentStepSchema, setCurrentStepSchema] = useState<{
    schema: RJSFSchema;
    uiSchema: Record<string, unknown>;
    title?: string;
  } | null>(null);
  const [currentFormData, setCurrentFormData] = useState<FlowFormData>({});
  const [accumulatedData, setAccumulatedData] = useState<FlowFormData>({});
  const [stepDataByKey, setStepDataByKey] = useState<Record<string, FlowFormData>>({});
  const [isInitializing, setIsInitializing] = useState(true);
  const [lastStepMappings, setLastStepMappings] = useState<FlowMappingsConfig>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [autoSkippedSteps, setAutoSkippedSteps] = useState<Set<string>>(new Set());

  const hasInitialized = useRef(false);
  const activeRequestId = useRef(0);
  const pendingAutoFillStep = useRef<{ stepIndex: number; dataToSend: FlowFormData } | null>(null);

  const isLastStep = currentStepIndex === stepKeys.length - 1;
  const hasPreviousStep = stepKeys.slice(0, currentStepIndex).some((key) => !autoSkippedSteps.has(key));

  const loadStepSchema = async (stepIndex: number, dataToSend: FlowFormData): Promise<void> => {
    const stepIdToCall = stepKeys[stepIndex];
    const bodyToSend = stepIndex === 0 ? {} : dataToSend;
    const requestId = ++activeRequestId.current;
    setStepError(null);

    const staticFormId = paramsConfig[stepIdToCall]?.reactJsonSchemaForm?.formId;
    if (AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(staticFormId)) {
      if (!audience && (isAudienceLoading || isAudienceFetching)) {
        pendingAutoFillStep.current = { stepIndex, dataToSend };
        return;
      }
      pendingAutoFillStep.current = null;
      const autoFillData = { audience_name: audience?.name ?? '', createdOn: audience?.createdOn ?? '' };
      const newAccumulatedData = { ...dataToSend, ...autoFillData };
      setAccumulatedData(newAccumulatedData);

      if (stepIndex === stepKeys.length - 1) {
        onComplete(newAccumulatedData, null);
      } else {
        const nextStepIndex = stepIndex + 1;
        setCurrentStepIndex(nextStepIndex);
        setCurrentFormData({});
        setIsFormValid(false);
        await loadStepSchema(nextStepIndex, newAccumulatedData);
      }
      return;
    }

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: connectorFlowId,
        stepId: stepIdToCall,
        audienceId: audienceId,
        connectorInstanceId,
        stepData: bodyToSend,
      }).unwrap();

      if (requestId !== activeRequestId.current) {
        return;
      }

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const translatedParamsConfig = translateI18nValues(paramsConfig, currentLanguage);

      const preparedStepConfig = translatedResponse?.configuration?.elements?.params?.[stepKeys[stepIndex]];
      const formSchema =
        preparedStepConfig?.reactJsonSchemaForm ||
        translatedResponse?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.reactJsonSchemaForm ||
        translatedParamsConfig[stepKeys[stepIndex]]?.reactJsonSchemaForm;

      /**
       * Mappings RJSF config from `prepare` (POST): try in order until `reactJsonSchemaForm` is found.
       * Keep in sync with FlowMappingsForm / TemplateSelectionStep after the flow completes.
       *
       * 1. `configuration.elements.mappings.reactJsonSchemaForm`
       * 2. `configuration.mappings.reactJsonSchemaForm`
       * 3. `mappings.reactJsonSchemaForm`
       */
      const mappingsSchema =
        translatedResponse?.configuration?.elements?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.configuration?.mappings?.reactJsonSchemaForm ||
        translatedResponse?.mappings?.reactJsonSchemaForm;
      const currentMappings: FlowMappingsConfig = mappingsSchema?.schema ? { schema: mappingsSchema.schema, uiSchema: mappingsSchema.uiSchema || {} } : null;
      if (currentMappings) {
        setLastStepMappings(currentMappings);
      }

      if (formSchema) {
        if (AUTO_FILL_AUDIENCE_PARAMS_FORM_IDS.includes(formSchema.formId)) {
          if (!audience && (isAudienceLoading || isAudienceFetching)) {
            pendingAutoFillStep.current = { stepIndex, dataToSend };
            return;
          }
          pendingAutoFillStep.current = null;
          const autoFillData = { audience_name: audience?.name ?? '', createdOn: audience?.createdOn ?? '' };
          const newAccumulatedData = { ...dataToSend, ...autoFillData };
          setAccumulatedData(newAccumulatedData);

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            setIsFormValid(false);
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        // Auto-skip steps that have only hidden fields (or no fields at all).
        // Hidden defaults come from the backend (addHiddenFieldToSchema), so we just
        // collect them and advance — no UI for the user to interact with.
        if (isSchemaAllHidden(formSchema)) {
          const autoData = collectDefaults(formSchema.schema);
          const newAccumulatedData = { ...dataToSend, ...autoData };
          setAccumulatedData(newAccumulatedData);
          setAutoSkippedSteps((prev) => new Set(prev).add(stepIdToCall));

          if (stepIndex === stepKeys.length - 1) {
            onComplete(newAccumulatedData, currentMappings);
          } else {
            const nextStepIndex = stepIndex + 1;
            setCurrentStepIndex(nextStepIndex);
            setCurrentFormData({});
            setIsFormValid(false);
            await loadStepSchema(nextStepIndex, newAccumulatedData);
          }
          return;
        }

        setCurrentStepSchema({
          schema: formSchema.schema,
          uiSchema: enhanceFlowStepUiSchema(
            (formSchema.uiSchema || {}) as Record<string, unknown>,
            htmlBodyFieldMode,
            formSchema.schema
          ),
          title: formSchema.title,
        });
        const currentStepKey = stepKeys[stepIndex];
        const existingData = stepDataByKey[currentStepKey] ?? {};
        setCurrentFormData(existingData);
        setIsFormValid(Object.keys(existingData).length > 0);
      }
    } catch (error: unknown) {
      if (requestId !== activeRequestId.current) {
        return;
      }
      const apiError = error as { data?: { message?: string } };
      const errorMessage = apiError?.data?.message || 'Something went wrong. Could not load this step.';
      setStepError(errorMessage);
      dispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
    }
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      setIsInitializing(true);
      await loadStepSchema(0, {});
      setIsInitializing(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (isAudienceLoading || isAudienceFetching) return;
    if (!pendingAutoFillStep.current) return;

    const pendingStep = pendingAutoFillStep.current;
    pendingAutoFillStep.current = null;
    loadStepSchema(pendingStep.stepIndex, pendingStep.dataToSend);
  }, [isAudienceLoading, isAudienceFetching, audience]);

  const handleNextStep = async () => {
    const newAccumulatedData = { ...accumulatedData, ...currentFormData };
    setAccumulatedData(newAccumulatedData);
    setStepDataByKey((prev) => ({ ...prev, [stepKeys[currentStepIndex]]: currentFormData }));

    if (isLastStep) {
      onComplete(newAccumulatedData, lastStepMappings);
    } else {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextStepIndex);
      setIsFormValid(false);
      await loadStepSchema(nextStepIndex, newAccumulatedData);
    }
  };

  const handlePreviousStep = async () => {
    let prevStepIndex = currentStepIndex - 1;
    while (prevStepIndex >= 0 && autoSkippedSteps.has(stepKeys[prevStepIndex])) {
      prevStepIndex -= 1;
    }
    if (prevStepIndex >= 0) {
      setCurrentStepIndex(prevStepIndex);
      const stepKey = stepKeys[prevStepIndex];
      const previousData = stepDataByKey[stepKey] ?? {};
      setCurrentFormData(previousData);
      setIsFormValid(Object.keys(previousData).length > 0);
      await loadStepSchema(prevStepIndex, accumulatedData);
    }
  };

  return (
    <div className={`w-full grid ${showTemplatePreview ? 'grid-cols-3' : 'grid-cols-1'} gap-6`}>
      <div className='space-y-6 col-span-2 border border-gray-200 rounded-2xl p-4'>
        {/* Step indicator */}
        <StepsIndication steps={stepKeys} currentStepIndex={currentStepIndex} />
        {/* Step title */}
        {currentStepSchema?.title && <h3 className='text-lg font-medium text-primary-900'>{currentStepSchema.title}</h3>}
        {stepError && <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{stepError}</div>}

        {/* Form */}
        <Loadable isLoading={isInitializing || isLoadingStep}>
          {currentStepSchema && (
            <div className='whatsapp-flow-rjsf rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
              <Form
                className='rjsf'
                tagName='div'
                id={id}
                noHtml5Validate
                schema={currentStepSchema.schema}
                uiSchema={currentStepSchema.uiSchema}
                formData={currentFormData}
                templates={templates}
                widgets={widgets}
                onChange={(event: IChangeEvent) => {
                  setCurrentFormData((event.formData as FlowFormData) ?? {});
                  setIsFormValid(event.errors.length === 0);
                }}
                validator={validator}
              >
                <Fragment />
              </Form>
            </div>
          )}
        </Loadable>

        {/* Navigation buttons */}
        <div className='flex justify-between pt-4'>
          <Button
            type='button'
            intent='secondary'
            onClick={handlePreviousStep}
            disabled={!hasPreviousStep}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              !hasPreviousStep ? 'cursor-not-allowed text-gray-400' : 'text-primary-600 hover:text-primary-700'
            }`}
          >
            {String(t('COMMON:previous' as any))}
          </Button>
          <Button
            type='button'
            intent='primary'
            onClick={handleNextStep}
            disabled={!isFormValid || isLoadingStep || isInAction}
            className={`text-sm font-medium ${!isFormValid || isLoadingStep ? 'cursor-not-allowed bg-gray-300 text-gray-500' : ''}`}
          >
            {isInAction ? (
              <Loading />
            ) : (
              <>
                {String(
                  isLastStep
                    ? (lastStepActionLabel ?? (t('COMMON:confirm' as any) as string))
                    : (t('COMMON:next' as any) as string)
                )}
              </>
            )}
          </Button>
        </div>
      </div>
      {showTemplatePreview && templatePreviewVariant === 'email' ? (
        <ErrorBoundary>
          <EmailTemplatePreview formData={currentFormData} />
        </ErrorBoundary>
      ) : null}
      {showTemplatePreview && templatePreviewVariant !== 'email' ? (
        <ErrorBoundary>
          <TemplatePreview formData={currentFormData} />
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
